/**
 * Handwriting -> notation recognition.
 *
 * This is deterministic geometry, not a black box. Every decision below is a
 * measurable property of the strokes (size relative to staff spacing, aspect
 * ratio, closure, vertical overlap), which means when it gets something wrong
 * you can see exactly why, and the confidence numbers it reports are real.
 *
 * Pipeline:
 *   A. per-stroke pass  - a stroke that is already unambiguously a notehead or a
 *                         barline becomes its own glyph immediately. This matters:
 *                         noteheads in a chord physically touch each other, so a
 *                         naive "merge things that are close" pass fuses a whole
 *                         chord into one blob.
 *   B. absorb           - leftover marks sitting inside a notehead are its fill.
 *   C. cluster the rest - stems and accidental fragments group by proximity.
 *   D. classify         - sharp / flat / natural / stem / dot / unknown.
 *   E. attach           - accidentals and stems bind to their notehead.
 *   F. pitch and group  - notehead centre -> staff position -> pitch; vertically
 *                         aligned noteheads become a chord.
 */

import { positionToPitch, yToExactPosition, type Clef, type StaffLayout } from '../theory/staff';
import type { Letter, Pitch } from '../theory/pitch';
import {
  boundsCenter,
  boundsHeight,
  boundsWidth,
  closure,
  strokeBounds,
  strokeLength,
  unionBounds,
  type Bounds,
  type Stroke,
} from './types';

export type GlyphKind =
  | 'noteheadFilled'
  | 'noteheadHollow'
  | 'stem'
  | 'sharp'
  | 'flat'
  | 'natural'
  | 'dot'
  | 'barline'
  | 'unknown';

export const GLYPH_LABELS: Record<GlyphKind, string> = {
  noteheadFilled: 'Filled notehead',
  noteheadHollow: 'Open notehead',
  stem: 'Stem',
  sharp: 'Sharp',
  flat: 'Flat',
  natural: 'Natural',
  dot: 'Dot',
  barline: 'Barline',
  unknown: 'Not recognised',
};

export interface Glyph {
  kind: GlyphKind;
  bounds: Bounds;
  strokeIds: string[];
  confidence: number;
  /** Short reason string, surfaced in the "why did it read it that way" panel. */
  reason: string;
}

export type Duration = 'w' | 'h' | 'q';

export interface RecognizedNote {
  pitch: Pitch;
  /** Staff position: 0 = bottom line. */
  position: number;
  duration: Duration;
  /** -1 flat, 0 natural (explicit), 1 sharp, null = nothing written. */
  writtenAccidental: number | null;
  confidence: number;
  x: number;
  y: number;
  strokeIds: string[];
}

export interface RecognitionResult {
  notes: RecognizedNote[];
  /** Vertically aligned notes, left to right. Each group is a chord or single note. */
  groups: RecognizedNote[][];
  glyphs: Glyph[];
  unrecognizedStrokeIds: string[];
  /** Lowest note confidence in the reading — what the UI shows as overall certainty. */
  confidence: number;
}

export interface RecognizeOptions {
  clef: Clef;
  layout: StaffLayout;
  /** Letter -> alteration implied by the key signature, applied when nothing is written. */
  keySignature?: Partial<Record<Letter, number>>;
  /** False on the live iPad pad, where accidentals use the reliable selector. */
  handwrittenAccidentals?: boolean;
}

// ------------------------------------------------------------------ geometry helpers

/** Gap between two boxes, or 0 when they overlap. */
function boxGap(a: Bounds, b: Bounds): number {
  const dx = Math.max(0, Math.max(a.minX - b.maxX, b.minX - a.maxX));
  const dy = Math.max(0, Math.max(a.minY - b.maxY, b.minY - a.maxY));
  return Math.hypot(dx, dy);
}

function contains(outer: Bounds, inner: Bounds, slack: number): boolean {
  return (
    inner.minX >= outer.minX - slack &&
    inner.maxX <= outer.maxX + slack &&
    inner.minY >= outer.minY - slack &&
    inner.maxY <= outer.maxY + slack
  );
}

function verticality(stroke: Stroke): number {
  const b = strokeBounds(stroke);
  const w = boundsWidth(b);
  const h = boundsHeight(b);
  if (h === 0) return 0;
  return h / (h + w);
}

/** Fraction of height that two boxes share vertically. */
function verticalOverlap(a: Bounds, b: Bounds): number {
  const overlap = Math.min(a.maxY, b.maxY) - Math.max(a.minY, b.minY);
  const shortest = Math.min(boundsHeight(a), boundsHeight(b));
  if (shortest <= 0) return 0;
  return Math.max(0, overlap) / shortest;
}

/**
 * Horizontal extent of the ink above a given y, walking segments rather than raw
 * samples so a fast, sparsely-sampled stroke measures the same as a slow one.
 */
function widthAbove(strokes: Stroke[], yLimit: number): number {
  let min = Infinity;
  let max = -Infinity;
  const consider = (x: number) => {
    if (x < min) min = x;
    if (x > max) max = x;
  };
  for (const s of strokes) {
    for (let i = 0; i < s.points.length; i++) {
      const p = s.points[i];
      if (p.y < yLimit) consider(p.x);
      if (i > 0) {
        const a = s.points[i - 1];
        if (a.y < yLimit !== p.y < yLimit && p.y !== a.y) {
          const t = (yLimit - a.y) / (p.y - a.y);
          consider(a.x + (p.x - a.x) * t);
        }
      }
    }
  }
  return max >= min ? max - min : 0;
}

/** How much ink there is relative to the outline of the bounding oval. */
function fillRatio(strokes: Stroke[], bounds: Bounds): number {
  const total = strokes.reduce((sum, s) => sum + strokeLength(s), 0);
  const perimeter = Math.PI * ((boundsWidth(bounds) + boundsHeight(bounds)) / 2);
  return perimeter > 0 ? total / perimeter : 0;
}

// ------------------------------------------------------------------ phase A

interface HeadCandidate {
  stroke: Stroke;
  bounds: Bounds;
}

/** Is this single stroke, on its own, unambiguously a notehead? */
function looksLikeNotehead(stroke: Stroke, space: number): boolean {
  const b = strokeBounds(stroke);
  const w = boundsWidth(b) / space;
  const h = boundsHeight(b) / space;
  if (h <= 0) return false;
  const aspect = w / h;
  if (w < 0.45 || w > 2.4) return false;
  if (h < 0.35 || h > 1.8) return false;
  if (aspect < 0.55 || aspect > 3.0) return false;
  // A straight dash has the right box but almost no ink relative to it.
  return fillRatio([stroke], b) > 0.7;
}

function looksLikeBarline(stroke: Stroke, space: number): boolean {
  const b = strokeBounds(stroke);
  return boundsWidth(b) / space < 0.55 && boundsHeight(b) / space >= 3.4;
}

/** Tall and thin — a stem, a barline, or the upright of a flat/sharp/natural. */
function looksLikeUpright(stroke: Stroke, space: number): boolean {
  const b = strokeBounds(stroke);
  return boundsWidth(b) / space < 0.7 && boundsHeight(b) / space >= 1.1;
}

/**
 * The loop of a flat is the same size and shape as a small notehead, so shape
 * alone cannot separate them. What distinguishes a flat is the upright: it sits
 * on the *left* of the loop and runs well *above* it. A stem, by contrast, either
 * rises from the right edge or falls from the left — never rises from the left.
 */
function isFlatLoop(candidate: Bounds, uprights: Bounds[], space: number): boolean {
  return uprights.some((v) => {
    const risesAbove = v.minY < candidate.minY - space * 0.7;
    const staysAtBottom = v.maxY < candidate.maxY + space * 0.5;
    const onTheLeft = (v.minX + v.maxX) / 2 < (candidate.minX + candidate.maxX) / 2;
    const touching = boxGap(v, candidate) <= space * 0.5;
    return risesAbove && staysAtBottom && onTheLeft && touching;
  });
}

// ------------------------------------------------------------------ phase C/D

function clusterStrokes(strokes: Stroke[], space: number): Stroke[][] {
  const boxes = strokes.map(strokeBounds);
  const parent = strokes.map((_, i) => i);
  const find = (i: number): number => (parent[i] === i ? i : (parent[i] = find(parent[i])));
  const union = (a: number, b: number) => {
    const ra = find(a);
    const rb = find(b);
    if (ra !== rb) parent[rb] = ra;
  };

  // Pencil strokes rarely meet as cleanly as mouse fixtures do. A fast crossbar
  // can stop just short of an upright, especially after Safari coalesces its
  // samples. 0.8 spaces still cannot bridge the normal accidental-to-note gap,
  // but it keeps the four parts of a human sharp in one glyph.
  const threshold = space * 0.8;
  for (let i = 0; i < strokes.length; i++) {
    for (let j = i + 1; j < strokes.length; j++) {
      if (boxGap(boxes[i], boxes[j]) <= threshold) union(i, j);
    }
  }

  const groups = new Map<number, Stroke[]>();
  strokes.forEach((stroke, i) => {
    const root = find(i);
    if (!groups.has(root)) groups.set(root, []);
    groups.get(root)!.push(stroke);
  });
  return [...groups.values()];
}

function classifyCluster(cluster: Stroke[], space: number): Glyph {
  const boxes = cluster.map(strokeBounds);
  const bounds = unionBounds(boxes);
  const w = boundsWidth(bounds) / space;
  const h = boundsHeight(bounds) / space;
  const strokeIds = cluster.map((s) => s.id);
  const aspect = h === 0 ? 99 : w / h;

  const glyph = (kind: GlyphKind, confidence: number, reason: string): Glyph => ({
    kind,
    bounds,
    strokeIds,
    confidence,
    reason,
  });

  if (w < 0.42 && h < 0.42) {
    return glyph('dot', 0.7, `Tiny mark, ${w.toFixed(2)} × ${h.toFixed(2)} spaces.`);
  }

  if (cluster.length === 1 && w < 0.55 && h >= 3.4) {
    return glyph('barline', 0.85, `Single vertical line ${h.toFixed(1)} spaces tall — spans the staff.`);
  }

  // --- accidentals: all three live in a 1-3 space tall band, taller than wide ---
  if (h >= 1.0 && h <= 3.2 && w <= 1.8 && aspect < 1.2 && cluster.length >= 1) {
    // A hand-drawn upright leans. Treat a stroke as upright when height still
    // clearly dominates width; requiring near-perfect verticality rejected
    // ordinary Pencil sharps drawn at a slant.
    const verticals = cluster.filter((s) => verticality(s) > 0.62);

    // Flat: the upper section is a bare vertical, the loop only occupies the lower half.
    const topBand = bounds.minY + boundsHeight(bounds) * 0.35;
    const boxWidth = boundsWidth(bounds);
    const topWidthRatio = boxWidth > 0 ? widthAbove(cluster, topBand) / boxWidth : 1;

    if (cluster.length <= 2 && topWidthRatio < 0.58) {
      return glyph(
        'flat',
        0.78,
        `Upper section spans only ${Math.round(topWidthRatio * 100)}% of the width, with a loop below — a flat.`,
      );
    }

    if (verticals.length >= 2) {
      const vboxes = verticals.map(strokeBounds).sort((a, b) => a.minX - b.minX);
      const first = vboxes[0];
      const last = vboxes[vboxes.length - 1];
      const overlap = verticalOverlap(first, last);
      const firstCentre = (first.minY + first.maxY) / 2;
      const lastCentre = (last.minY + last.maxY) / 2;
      const centreOffset = Math.abs(firstCentre - lastCentre) / Math.max(1, Math.min(boundsHeight(first), boundsHeight(last)));
      // A sharp's two verticals run side by side; a natural's are offset, the left
      // one sitting higher than the right.
      if (overlap > 0.48 && centreOffset < 0.38) {
        return glyph(
          'sharp',
          0.8,
          `Two parallel verticals overlapping ${Math.round(overlap * 100)}%, crossed by ${cluster.length - verticals.length} stroke(s) — a sharp.`,
        );
      }
      return glyph(
        'natural',
        0.72,
        `Two offset verticals sharing only ${Math.round(overlap * 100)}% of their height — a natural.`,
      );
    }
  }

  // --- stem ---
  if (cluster.length === 1 && w < 0.7 && h >= 1.3 && h < 3.4) {
    return glyph('stem', 0.8, `Thin vertical stroke, ${h.toFixed(1)} spaces tall.`);
  }

  // --- notehead and stem written without lifting the pen ---
  if (cluster.length === 1 && h > 1.5 && w <= 2.4) {
    const split = splitHeadAndStem(cluster[0], bounds, space);
    if (split) return split;
  }

  return glyph(
    'unknown',
    0.2,
    `${cluster.length} stroke(s), ${w.toFixed(1)} × ${h.toFixed(1)} spaces — no rule matched.`,
  );
}

/**
 * Plenty of people write a note without lifting the pen: loop the head, then run
 * the stem straight up. Look for a band roughly one space tall holding most of
 * the ink; that band is the head.
 */
function splitHeadAndStem(stroke: Stroke, bounds: Bounds, space: number): Glyph | null {
  const bandHeight = space * 1.0;
  const total = strokeLength(stroke);
  if (total <= 0) return null;

  let best: { y: number; share: number } | null = null;
  const steps = 12;
  for (let i = 0; i <= steps; i++) {
    const top = bounds.minY + ((boundsHeight(bounds) - bandHeight) * i) / steps;
    const bottom = top + bandHeight;
    let inBand = 0;
    for (let j = 1; j < stroke.points.length; j++) {
      const a = stroke.points[j - 1];
      const b = stroke.points[j];
      const mid = (a.y + b.y) / 2;
      if (mid >= top && mid <= bottom) inBand += Math.hypot(b.x - a.x, b.y - a.y);
    }
    const share = inBand / total;
    if (!best || share > best.share) best = { y: (top + bottom) / 2, share };
  }

  if (!best || best.share < 0.5) return null;

  return {
    kind: 'noteheadFilled',
    bounds: {
      minX: bounds.minX,
      maxX: bounds.maxX,
      minY: best.y - bandHeight / 2,
      maxY: best.y + bandHeight / 2,
    },
    strokeIds: [stroke.id],
    confidence: 0.62,
    reason: `Head and stem in one motion — ${Math.round(best.share * 100)}% of the ink sits in a one-space band.`,
  };
}

// -------------------------------------------------------------------- assembly

export function recognize(strokes: Stroke[], options: RecognizeOptions): RecognitionResult {
  const { layout, clef, keySignature = {}, handwrittenAccidentals = true } = options;
  const space = layout.lineGap;

  const writing = strokes.filter((s) => s.tool === 'pen');
  if (!writing.length) {
    return { notes: [], groups: [], glyphs: [], unrecognizedStrokeIds: [], confidence: 0 };
  }

  // --- A. strokes that are a complete glyph on their own -------------------
  const heads: HeadCandidate[] = [];
  const pool: Stroke[] = [];
  const standaloneGlyphs: Glyph[] = [];

  const uprights = writing.filter((s) => looksLikeUpright(s, space)).map(strokeBounds);

  for (const stroke of writing) {
    if (looksLikeBarline(stroke, space)) {
      standaloneGlyphs.push({
        kind: 'barline',
        bounds: strokeBounds(stroke),
        strokeIds: [stroke.id],
        confidence: 0.85,
        reason: `Vertical line ${(boundsHeight(strokeBounds(stroke)) / space).toFixed(1)} spaces tall — spans the staff.`,
      });
    } else if (looksLikeNotehead(stroke, space) && !isFlatLoop(strokeBounds(stroke), uprights, space)) {
      heads.push({ stroke, bounds: strokeBounds(stroke) });
    } else {
      pool.push(stroke);
    }
  }

  // --- B. leftover marks inside a notehead are its fill ---------------------
  const absorbed = new Map<HeadCandidate, Stroke[]>();
  const remaining: Stroke[] = [];
  for (const stroke of pool) {
    const box = strokeBounds(stroke);
    const host = heads.find((h) => contains(h.bounds, box, space * 0.15));
    if (host) {
      if (!absorbed.has(host)) absorbed.set(host, []);
      absorbed.get(host)!.push(stroke);
    } else {
      remaining.push(stroke);
    }
  }

  const headGlyphs: Glyph[] = heads.map((head) => {
    const extra = absorbed.get(head) ?? [];
    const all = [head.stroke, ...extra];
    const ratio = fillRatio(all, head.bounds);
    const loop = closure(head.stroke);
    // One clean closed loop with no scribble inside it = an open notehead.
    const hollow = extra.length === 0 && loop < 0.42 && ratio < 1.45;
    return {
      kind: hollow ? 'noteheadHollow' : 'noteheadFilled',
      bounds: head.bounds,
      strokeIds: all.map((s) => s.id),
      confidence: hollow ? 0.82 : 0.8,
      reason: hollow
        ? `Single closed loop, ink ${ratio.toFixed(2)}× the outline — open notehead.`
        : `${all.length} stroke(s), ink ${ratio.toFixed(2)}× the outline — filled notehead.`,
    };
  });

  // --- C/D. cluster and classify what is left ------------------------------
  const clusterGlyphs = remaining.length
    ? clusterStrokes(remaining, space).map((cluster) => classifyCluster(cluster, space))
    : [];

  const glyphs = [...headGlyphs, ...clusterGlyphs, ...standaloneGlyphs].sort(
    (a, b) => a.bounds.minX - b.bounds.minX,
  );

  const noteheads = glyphs.filter((g) => g.kind === 'noteheadFilled' || g.kind === 'noteheadHollow');
  const stems = glyphs.filter((g) => g.kind === 'stem');
  const accidentals = handwrittenAccidentals
    ? glyphs.filter((g) => g.kind === 'sharp' || g.kind === 'flat' || g.kind === 'natural')
    : [];

  // --- E/F. pitch, accidentals, duration, grouping --------------------------
  const notes: RecognizedNote[] = noteheads.map((head) => {
    const center = boundsCenter(head.bounds);
    const exact = yToExactPosition(layout, center.y);
    const position = Math.round(exact);
    // How cleanly the head landed on a line or space. A head halfway between two
    // positions is genuinely ambiguous and should say so.
    const snapConfidence = Math.max(0, 1 - Math.abs(exact - position) * 1.8);

    // An accidental belongs to the notehead immediately to its right, on the same line.
    const accidental = accidentals
      .map((acc) => {
        const accCenter = boundsCenter(acc.bounds);
        return {
          acc,
          dx: center.x - accCenter.x,
          dy: Math.abs(center.y - accCenter.y),
        };
      })
      .filter(({ dx, dy }) => dx > 0 && dx < space * 3.4 && dy < space * 1.15)
      .sort((a, b) => a.dy - b.dy || a.dx - b.dx)[0]?.acc;

    const writtenAccidental =
      accidental === undefined ? null : accidental.kind === 'sharp' ? 1 : accidental.kind === 'flat' ? -1 : 0;

    const provisional = positionToPitch(clef, position, 0);
    const alter = writtenAccidental !== null ? writtenAccidental : (keySignature[provisional.letter] ?? 0);
    const pitch = positionToPitch(clef, position, alter);

    const hasStem = stems.some((stem) => {
      const horizontallyTouching =
        stem.bounds.minX <= head.bounds.maxX + space * 0.5 &&
        stem.bounds.maxX >= head.bounds.minX - space * 0.5;
      const verticallyNear =
        stem.bounds.maxY >= head.bounds.minY - space * 3.8 &&
        stem.bounds.minY <= head.bounds.maxY + space * 3.8;
      return horizontallyTouching && verticallyNear;
    });

    const duration: Duration = head.kind === 'noteheadFilled' ? 'q' : hasStem ? 'h' : 'w';

    return {
      pitch,
      position,
      duration,
      writtenAccidental,
      confidence: Math.min(head.confidence, snapConfidence),
      x: center.x,
      y: center.y,
      strokeIds: [...head.strokeIds, ...(accidental?.strokeIds ?? [])],
    };
  });

  notes.sort((a, b) => a.x - b.x);

  // Vertically aligned noteheads are one chord. Real handwriting is never perfectly
  // aligned, so allow some horizontal drift before starting a new group.
  const groups: RecognizedNote[][] = [];
  for (const note of notes) {
    const last = groups[groups.length - 1];
    const anchor = last ? last.reduce((sum, n) => sum + n.x, 0) / last.length : 0;
    if (last && Math.abs(note.x - anchor) < space * 1.5) last.push(note);
    else groups.push([note]);
  }
  for (const group of groups) group.sort((a, b) => a.position - b.position);

  const unrecognizedStrokeIds = glyphs.filter((g) => g.kind === 'unknown').flatMap((g) => g.strokeIds);

  const confidence = notes.length ? notes.reduce((min, n) => Math.min(min, n.confidence), 1) : 0;

  return { notes, groups, glyphs, unrecognizedStrokeIds, confidence };
}

/** The honest, published list of what the engine can currently read. */
export const SUPPORTED_GLYPHS: { kind: GlyphKind; how: string }[] = [
  { kind: 'noteheadFilled', how: 'A scribbled-in oval about one space tall.' },
  { kind: 'noteheadHollow', how: 'A single closed loop, not filled in.' },
  { kind: 'stem', how: 'A vertical line 1.3–3.4 spaces tall touching a head.' },
  { kind: 'barline', how: 'A vertical line taller than 3.4 spaces.' },
];

// -------------------------------------------------- adaptive grouping window

/**
 * How long to wait after a stroke before reading the page.
 *
 * A sharp is four strokes and a natural is three, so a fixed window is a bet on
 * how fast the learner writes. At 160ms it is a bad bet: pause for a quarter of a
 * second between the first upright and the second and recognition fires on half
 * a sharp, reads a lone vertical as a stem, and hangs it off the nearest
 * notehead. The learner then finishes the glyph and watches the app change its
 * mind — which reads as the app being unreliable rather than early.
 *
 * So the window is short when the ink already says something complete, and
 * longer when the last thing drawn is exactly the sort of mark that is usually
 * the *start* of something. It is never long enough to feel like waiting: the
 * ink itself is on screen within a frame either way, and only the reading is
 * deferred.
 */
export const BASE_GROUPING_MS = 160;
export const EXTENDED_GROUPING_MS = 540;

export function groupingDelay(strokes: Stroke[], layout: StaffLayout): number {
  const writing = strokes.filter((s) => s.tool === 'pen');
  const last = writing[writing.length - 1];
  if (!last) return BASE_GROUPING_MS;

  const space = layout.lineGap;

  // Follow recognition's own order, or this asks a different question from the
  // one the recogniser will answer. Phase A settles a clean notehead or a
  // full-height barline on its own, before any clustering happens — reaching
  // `classifyCluster` with one of those returns "unknown", and every note the
  // learner wrote would sit through the long window for nothing.
  if (looksLikeNotehead(last, space) || looksLikeBarline(last, space)) return BASE_GROUPING_MS;

  // Otherwise: the cluster the newest stroke belongs to, by the rule recognition
  // uses to group ink.
  const cluster =
    clusterStrokes(writing, space).find((group) => group.some((s) => s.id === last.id)) ?? [last];
  const glyph = classifyCluster(cluster, space);

  // Two uprights are not a finished sharp or natural. The old 160ms path read
  // them before the learner added both crossbars, which is why a real ♯ could
  // turn into the wrong symbol while it was still being drawn.
  const uprightCount = cluster.filter((stroke) => verticality(stroke) > 0.62).length;
  if (uprightCount >= 2 && cluster.length - uprightCount < 2) return EXTENDED_GROUPING_MS;

  // Nothing matched: the most likely reason is that the glyph is not finished.
  if (glyph.kind === 'unknown') return EXTENDED_GROUPING_MS;

  /*
   * A single upright is the ambiguous one. It is a perfectly good stem, and it
   * is also the first stroke of a sharp, of a natural, and of a flat. Waiting
   * costs a fifth of a second; not waiting costs a wrong note that then visibly
   * changes.
   */
  if (cluster.length === 1 && (glyph.kind === 'stem' || glyph.kind === 'barline')) {
    const height = boundsHeight(strokeBounds(last)) / space;
    if (height >= 1.0 && height <= 3.2) return EXTENDED_GROUPING_MS;
  }

  return BASE_GROUPING_MS;
}
